/*!
// <copyright file="DynamicsApp.ts" company="Microsoft">
//     Copyright (c) Microsoft Corporation.  All rights reserved.
// </copyright>
*/

/* global Office, AuthenticationContext, resources, util, $format, telemetry, messaging, appletMessaging, appState, appElements, dialogs, notifications, notificationHandling, messageBoxHandling, operationHandling, telemetryLogging, appletInteraction, authentication, dynamicsOfficeBuild */

import * as $ from "jquery";
import "jquery-ui/ui/keycode";
import { AccountInfo, AuthError } from "@azure/msal-browser";
import { telemetry } from "@ax/office-cdn-app-telemetry";
import { util, $format, appletMessaging, messaging } from "@ax/office-shared-lib";
import { JQueryThrottlingHandler } from '@ax/office-shared-lib/dist/Throttling';
import { resources, setTextDirection, labelLoader } from "@ax/office-cdn-app-resources";
import { appState } from "./AppState";
import { appletInteraction } from "./AppletInteraction";
import { appElements } from "./Elements";
import { dialogs } from "./Dialogs";
import { authentication } from "./Authentication";
import { notificationHandling } from "./NotificationHandling";
import { operationHandling } from "./OperationHandling";
import { messageBoxHandling } from "./MessageBoxHandling";
import { telemetryLogging } from "./TelemetryLogging";
import { LocaleInfo, isIndirectCulture, loadGlobalizeCulture, LabelLoader as BaseLabelLoader, getSupportedLocale } from "@ax/office-resources-common";
import { EnhancedConfigResponseHandler } from "./EnhancedConfigResponseHandler";

export namespace app {

    /** The path to the root of the application. */
    export let pathToRoot = "../";

    /** The format used to pull in the main add-in stylesheet. */
    export let addinStylesheetFormat = "App/{0}DynamicsApp.css";

    /** The version of the executing application. */
    export const version = 1;

    /** The key within the Office settings that the runtime configuration is stored. */
    export const runtimeConfigKey = "RuntimeConfig";

    /** The key within the Office settings that the features configuration is stored. */
    export const featuresKey = "Features";

    /** The key within the server configuration headers that the environment name is stored. */
    export const environmentHeaderName = "BCEnvironment";

    /** The display name for the environment header within the server configuration. */
    export const serverConfigEnvironmentDisplayName = "Environment";

    /** The key within the server configuration headers that the company name is stored. */
    export const companyHeaderName = "Company";

    /** List of instrumentation keys for which telemetry should not be enabled */
    export const disabledInstrumentationKeys = [
        "",
        "00000000-0000-0000-0000-000000000000",
        "587e4c23-9ab5-4074-8674-4a31688947b7",
        "0e9ff251-74c0-4b3f-8466-c5345e5d4933",
        "DisableTelemetry=true",
        "InstrumentationKey=00000000-0000-0000-0000-000000000000",
    ];

    /** The resource div to contain authentication and resource frames. */
    export let resourceDiv: JQuery;

    /** The applet div to contain applet frames. */
    export let appletDiv: JQuery;

    /** The applet title heading element. */
    export let appletTitle: JQuery;

    /** The list element that contains applet selection items. */
    export let appletSelectionList: JQuery;

    /** The sign in description span. */
    export let _signInDescriptionSpan: JQuery;

    /** Title in banner for copilot for finance. */
    export let _copilotForFinanceBannerTitle: JQuery;

    /** Link in copilot for finance banner. */
    export let _copilotForFinanceBannerLink: JQuery;

    /** Description in copilot for finance banner. */
    export let _copilotForFinanceBannerBodyText: JQuery;

    /** Image element for Copilot for Finance logo*/
    export let _copilotForFinanceBannerLogo: JQuery;

    /** The sign in anchor. */
    export let _signInLink: JQuery;

    /** The sign out anchor. */
    export let _signOutLink: JQuery;

    /** The unauthorized description span. */
    export let _unauthorizedDescriptionSpan: JQuery;

    /** The unauthorized sign out button. */
    export let _unauthorizedSignOutButton: JQuery;

    /** The user menu.*/
    export let _userMenu: JQuery;

    /** The user menu header button. */
    export let _userHeader: JQuery;

    /** The user header text element.*/
    export let _userHeaderText: JQuery;

    /** The options button. */
    export let optionsButton: JQuery = undefined;

    /** The options dialog, its elements, and its event handlers. */
    export namespace optionsDialog {

        /** The id of the options dialog element. */
        export const optionsDialogId = "optionsDialog";

        /** The heading element. */
        export let heading: JQuery;

        /** The Dynamics App options. */
        export namespace dynamicsAppOptions {

            /** The Dynamics App options' heading element. */
            export let heading: JQuery;

            /** The Dynamics App general options group. */
            export namespace generalOptionsGroup {

                /** The Dynamics App general options group container. */
                export let container: JQuery;

                /** The Dynamics App general options group's heading element. */
                export let heading: JQuery;

                /** The server label. */
                export let serverLabel: JQuery;

                /** The server input element. */
                export let serverInput: JQuery;

                /** The language label. */
                export let languageLabel: JQuery;

                /** The language input element. */
                export let languageInput: JQuery;
            }

            /** The Dynamics App applet configuration group. */
            export namespace advancedOptionsGroup {

                /** The Dynamics App applet configuration group's heading element. */
                export let heading: JQuery;

                /** The applet configuration button. */
                export let appletConfigurationButton: JQuery;
            }

            /** The Dynamics App telemetry options group. */
            export namespace telemetryOptionsGroup {

                /** The correlation ID input. */
                export let correlationIdInput: JQuery;
            }
        }

        /** The cancel button element. */
        export let cancelButton: JQuery;

        /** The OK button element. */
        export let okButton: JQuery;

        /** The element that contains applet specific options. */
        export let appletOptionsContent: JQuery;

        /**
         * Opens the options dialog and initializes the dialog fields with the current settings.
         */
        export function open(): void {
            app.optionsDialog.dynamicsAppOptions.generalOptionsGroup.serverInput.val(appState.State.getConnection());
            app.optionsDialog.dynamicsAppOptions.generalOptionsGroup.languageInput.val((appState.State.Settings.Language || "").toUpperCase());
            app.optionsDialog.dynamicsAppOptions.telemetryOptionsGroup.correlationIdInput.val((appState.State.ServerConfig && appState.State.Features.LogTelemetry) ? appState.State.CorrelationID : "");

            app.updateHeaderOptionsDOM();
            appletInteraction.updateAllAppletOptionsDOMs();

            // Collapse option sections for applets
            if (appletInteraction.appletStack !== undefined) {
                for (var appletInformation of appletInteraction.appletStack) {
                    var appletOptionSection = util.findElementById($format(appletInteraction._IdentifierFormats.AppletOptionsElement, appletInformation.AppletID));
                    if (util.doesElementExist(appletOptionSection)) {
                        appletOptionSection.attr(appElements.GeneralAttributes.expanded, "false");
                        appletOptionSection.find(".optionsHeader button").attr(util.AriaAttributes.Expanded, "false");
                    }
                };
            }

            dialogs.handler.openDialog(app.optionsDialog.optionsDialogId);
        }

        /**
         * Opens the options dialog with the Data Connector options expanded and initializes the dialog fields with the current settings.
         */
        export function openWithDataConnectorOptionsExpanded(): void {
            app.optionsDialog.dynamicsAppOptions.generalOptionsGroup.serverInput.val(appState.State.getConnection());
            app.optionsDialog.dynamicsAppOptions.generalOptionsGroup.languageInput.val((appState.State.Settings.Language || "").toUpperCase());
            app.optionsDialog.dynamicsAppOptions.telemetryOptionsGroup.correlationIdInput.val((appState.State.ServerConfig && appState.State.Features.LogTelemetry) ? appState.State.CorrelationID : "");

            app.updateHeaderOptionsDOM();
            appletInteraction.updateAllAppletOptionsDOMs();

            // Collapse option sections for applets except for the data connector applet.
            if (appletInteraction.appletStack !== undefined) {
                for (var appletInformation of appletInteraction.appletStack) {
                    var appletOptionSection = util.findElementById($format(appletInteraction._IdentifierFormats.AppletOptionsElement, appletInformation.AppletID));
                    if (util.doesElementExist(appletOptionSection)) {
                        if (appletInformation.AppletID === "Microsoft.Dynamics.Platform.Integration.Office.DataConnectorApplet") {
                            appletOptionSection.attr(appElements.GeneralAttributes.expanded, "true");
                            appletOptionSection.find(".optionsHeader button").attr(util.AriaAttributes.Expanded, "true");
                        }
                        else {
                            appletOptionSection.attr(appElements.GeneralAttributes.expanded, "false");
                            appletOptionSection.find(".optionsHeader button").attr(util.AriaAttributes.Expanded, "false");
                        }
                    }
                };
            }

            dialogs.handler.openDialog(app.optionsDialog.optionsDialogId);
        }

        /**
         * A jQuery event handler that saves the settings in the options dialog.
         * @param event The OK button click event.
         */
        export function okClicked(event: JQuery.ClickEvent<HTMLElement>): void {
            app.updateSettings();
        }

        /**
         * A jQuery event handler that closes the options dialog without saving.
         */
        export function close(): void {
            dialogs.handler.closeActiveDialog(app.optionsDialog.optionsDialogId);
        }

        /**
         * A jQuery event handler that applies the loaded applet state to the applet configuration dialog and opens the dialog.
         * @param event The configure applet button click event.
         */
        export function configureAppletClicked(event: JQuery.ClickEvent<HTMLElement>): void {
            appletInteraction.appletConfiguration.resetConfigurationDialogValues();
            dialogs.handler.openDialog(app.appletConfigurationDialog.dialogID);
        }
    }

    /** The applet configuration dialog, its elements, and its event handlers. */
    export namespace appletConfigurationDialog {

        /** The ID of the applet configuration dialog. */
        export const dialogID = "appletConfigDialog";

        /** The dialog's heading element. */
        export let heading: JQuery;

        /** The applet configuration dialog's container elements. */
        export namespace configDialogContainer {

            /** The dialog container's heading element. */
            export let heading: JQuery;

            /** The dialog container's content element. */
            export let configContent: JQuery;
        }

        /** The dialog's OK button. */
        export let okButton: JQuery;

        /** The dialog's cancel button. */
        export let cancelButton: JQuery;

        /**
         * A jQuery event handler that applies the user's applet load / unload selections and closes
         * @param event The OK button clicked event.
         */
        export function okClicked(event: JQuery.ClickEvent<HTMLElement>): void {
            appletInteraction.appletConfiguration.applyAppletConfiguration();
        }

        /**
         * A jQuery event handler that closes the dialog.
         * @param event The close button clicked event.
         */
        export function closeClicked(event: JQuery.ClickEvent<HTMLElement>): void {
            dialogs.handler.closeActiveDialog(app.appletConfigurationDialog.dialogID);
            var appletConfigurationbutton = util.findElementById("appletConfigurationButton");
            util.setFocus(appletConfigurationbutton);
        }
    }

    /** Contains constants related to header options. */
    export namespace DataHeaderOptionConstants {

        /** The attribute used to annotate a data header option. */
        export const DataHeaderOptionsAttribute = "data-header";

        /** The selector used to find all data header options. */
        export const DataHeaderOptionsSelector = "[data-header]";
    }

    /** The no server text element. */
    export let noServerText: JQuery;

    /** The no server link. */
    export let noServerLink: JQuery;

    /** The no applet link. */
    export let noAppletLink: JQuery;

    /** The IDs for controls known by the app. */
    export namespace ControlIDs {

        /** The ID of the resource div. */
        export const resourceDiv = "resourceDiv";

        /** The ID of the applet div. */
        export const appletDiv = "appletDiv";

        /** The ID of the applet header container element. */
        export const appletHeaderContainer = "appletHeaderContainer";

        /** The ID of the applet title span. */
        export const appletTitle = "appletHeader";

        /** The ID of the applet selection list. */
        export const appletSelectionList = "appletSelectionList";

        /** The ID of the content div. */
        export const contentDiv = "content";

        /** The ID of the sign in link. */
        export const signInLink = "signInLink";

        /** The ID of the unauthorized description span element. */
        export const unauthorizedDescriptionSpan = "unauthorizedDescription";

        /** The ID of the unauthorized sign out button. */
        export const unauthorizedSignOutButton = "unauthorizedSignOutButton";

        /** The ID of the notifications div. */
        export const notificationsDiv = "notifications";

        /** The ID of the app buttons div. */
        export const appButtonsDiv = "appButtons";

        /** The ID of the footer div. */
        export const footerDiv = "footer";

        /** The ID of the options button. */
        export const optionsButton = "optionsButton";

        /** The ID of the user header. */
        export const userHeader = "userHeader";

        /** The ID of the user header text element. */
        export const userHeaderText = "userHeaderText";

        /** The ID of the user menu. */
        export const userMenu = "userMenu";

        /** The ID of the options element meant to contain applet option content. */
        export const appletOptionsContent = "appletOptionsContent";

        /** The ID of the generalContent div. */
        export const generalContentDiv = "generalContent";

        /** The IDs relating to the applet configuration dialog. */
        export namespace appletConfigurationDialog {

            /** The ID of the dialog container div. */
            export const configDialogContainer = "appletConfigDialogContainer";

            /** The ID of the dialog applet content div. */
            export const configContent = "appletConfigContent";
        }

        /** The ID of the 'no applet' splash screen div. */
        export const noAppletsSplashScreen = "noAppletSplashScreen";
    }

    /**
     * Handles uncaught errors, displaying the error to the user.
     * @param message The error message.
     * @param url The URL where the error was raised.
     * @param line The line number where the error was raised.
     * @param column The column number where the error was raised.
     * @param error The error that occurred.
     * @returns A value indicating if the error was handled; if false the default error handler is subsequently run.
     */
    export function handleGlobalError(message: string, url: string, line: number, column: number, error: any): boolean {
        telemetry.errors.unhandledAppError.show(
            resources.dynamicsApp.unhandledErrorOccurredMessage(),
            message,
            url,
            line,
            column,
            JSON.stringify(error),
            error?.stack,
            {
                Details: message,
                IncludeCorrelationID: true,
                TelemetryProperties: { TelemetryType: util.telemetry.Type.Exception }
            });

        // Don't allow the default handler to run
        return true;
    }

    /**
     * Gets the authentication namespace from the parent window.
     * @returns the authentication namespace from the parent window.
     */
    export function getParentAuthenticationNamespace(): any {
        return (window.parent as any).authentication;
    }

    /**
     * Determines if the add-in is framed withing the add-in (MSAL.js frame).
     * @returns A value indicating if the add-in is framed withing the add-in (MSAL.js frame).
     */
    export function isFramed(): boolean {
        return window.parent !== window;
    }

    /** Represents the components required to be ready before the add-in can start. */
    export enum RequiredComponents {

        /** The authentication component. */
        Authentication,

        /** The Office component. */
        Office,
    }

    /** The components that are ready. */
    export const readyComponents: util.PropertyBag<boolean> = {};

    /**
     * Indicates that the provided component is ready, initializing the add-in once all require components are ready.
     * @param component The component that is ready.
     */
    export function componentReady(component: RequiredComponents): void {
        readyComponents[component] = true;
        if (readyComponents[RequiredComponents.Authentication] && readyComponents[RequiredComponents.Office]) {
            app.initialize(app.onInitialized);
        }
    }

    /** Initializes the application once the document is ready. */
    export async function onDocumentReady(): Promise<void> {

        function continueStartup() {

            // Schedule the add-in to initialize
            window.setTimeout(
                () => {
                    app.componentReady(app.RequiredComponents.Authentication);
                },
                0);
        }

        continueStartup();
    }

    /**
     * Unauthenticates the app, but doesn't sign it out. This method adjusts the UX appropriately to indicate the app isn't
     * authenticated, and unloads all applets and resources "softly" (retaining their settings).
     * @param callback The callback to perform once the app has been unauthenticated.
     */
    export function unauthenticate(callback: () => void, unauthenticatedState: appState.AuthenticationState = appState.AuthenticationState.notAuthenticated): void {

        // Save the app's settings prior to unauthenticating
        app.saveSettings(function settingsSaved() {

            // Unload applets and resources
            appletInteraction.unloadAllApplets({ softUnload: true });
            appletInteraction.unloadAllResources();

            // Adjust state to initial non-authenticated state
            appState.State.RegistrationsRetrieved = false;
            appState.State.ManualSignInAttempted = false;
            appState.State.AuthenticationState = unauthenticatedState;
            app.updateUIFromState();

            // Call the provided callback
            callback();
        });
    }

    /**
     * Sets the app state's correlation ID.
     * @returns A value indicating if accessing storage was successful.
     */
    export function _setCorrelationID(): boolean {
        let storageSupported = true;
        try {
            appState.State.CorrelationID = sessionStorage.DynAppCorrelationID ? sessionStorage.DynAppCorrelationID : util.generateGuid();
            sessionStorage.DynAppCorrelationID = appState.State.CorrelationID;
            localStorage.DynAppCorrelationID = appState.State.CorrelationID;
        } catch (e) {
            appState.State.CorrelationID = util.generateGuid();
            storageSupported = false;
        }
        return storageSupported;
    }

    /**
     * Creates the add-in state.
     * @returns The newly created add-in state.
     */
    export function createState(): appState.ApplicationState<appState.AddinSettings> {
        return new appState.ApplicationState();
    }

    /**
     * Common initialization function (to be called from each page).
     * @param completeCallback The void, no arg callback that is performed once initialization is complete. Note that the state object is not marked as initialized as a result
     * of calling this method, the caller is responsible for doing so.
     */
    export function initialize(completeCallback: () => void): void {

        telemetry.initialize(operationHandling, telemetryLogging, notificationHandling);

        // Find the content div
        appElements.contentDiv = util.findElementById(app.ControlIDs.contentDiv);

        // Initialize state
        appState.State = app.createState();

        // Establish a correlation ID for this app instance
        let storageSupported = app._setCorrelationID();

        // Load configuration and features
        appState.State.Settings = app.loadConfiguration();
        appState.State.Features = app.loadFeatures();

        var desiredLanguage = appState.State.Settings.Language || Office.context.displayLanguage;
        desiredLanguage = getSupportedLocale(desiredLanguage);

        telemetry.events.appInit.log(
            appState.State.Settings.DocumentId,
            appState.State.Settings.DateCreated,
            appState.State.Settings.GenerationActivityId);

        let loader = app.getLabelLoader();
        let promise = Promise.all([
            loadGlobalizeCulture(desiredLanguage),
            loader.initialize()
        ])
            .then((results) => {
                // Wait for the default language to be loaded before loading the desired language
                return loader.loadLabels(desiredLanguage)
            })
            .then((actualLanguage) => {
                loader.CurrentLocale = actualLanguage;
            })
            .catch(console.log.bind(console))
            .then(() => {

                // The add-in expects to work with an upper case locale, so ensure it's upper case
                loader.CurrentLocale = loader.CurrentLocale && loader.CurrentLocale.toUpperCase();

                // Set the working language into the app's state object.
                appState.State.Language = loader.CurrentLocale;
                if ((appState.State.Settings.Language || "").toUpperCase() !== loader.CurrentLocale) {
                    appState.State.Settings.Language = loader.CurrentLocale;
                    app.saveSettings();
                }

                if (!storageSupported) {
                    appElements.contentDiv.empty();

                    // Load the Excel theme style sheet; none of the colors will be used, but the appropriate sizing and fonts will be applied.
                    const defaultStyleSheetName = require("@ax/office-shared-lib/less/ExcelTheme.less");
                    util.addStyleSheet(defaultStyleSheetName, function themeStyleSheetAdded(): void {
                        appElements.contentDiv.text(resources.dynamicsApp.unsupportedStorage());
                        appElements.contentDiv.attr(appElements.ContentAttributes.systemNotSupported, "true");
                    });
                    return;
                }

                setTextDirection(appState.State.Language);

                // Apply the global error handler
                window.onerror = app.handleGlobalError;

                // Determine the current application by checking existence of type members only supported by a single application
                // See https://msdn.microsoft.com/en-us/library/office/jj220054(v=office.15).aspx for details
                if (Office.context.document.bindings.addFromPromptAsync) {
                    // addFromPromptAsync is only supported by Excel
                    appState.State.CurrentApplication = util.ApplicationType.Excel;
                    appState.State.OfficeVersion = "Excel" in window ? util.OfficeVersion.Release : util.OfficeVersion.Legacy;
                } else if (Office.context.document.customXmlParts) {
                    // customXmlParts is only supported by Word
                    appState.State.CurrentApplication = util.ApplicationType.Word;
                    appState.State.OfficeVersion = "Word" in window ? util.OfficeVersion.Release : util.OfficeVersion.Legacy;
                }

                app.resourceDiv = util.findElementById(app.ControlIDs.resourceDiv, appElements.contentDiv);
                app.appletDiv = util.findElementById(app.ControlIDs.appletDiv, appElements.contentDiv);
                var appletHeaderContainer = util.findElementById(app.ControlIDs.appletHeaderContainer, appElements.contentDiv);
                app.appletTitle = util.findElementById(app.ControlIDs.appletTitle, appletHeaderContainer);
                app.appletSelectionList = util.findElementById(app.ControlIDs.appletSelectionList, appletHeaderContainer);
                app._signInDescriptionSpan = $("#signInDescription");
                app._copilotForFinanceBannerTitle = $("#bannerTitle");
                app._copilotForFinanceBannerLink = $("#copilotForFinanceBannerLink");
                app._copilotForFinanceBannerBodyText = $("#bannerBodyText");
                app._copilotForFinanceBannerLogo = $("#bannerLogo");
                app._signInLink = util.findElementById(app.ControlIDs.signInLink, appElements.contentDiv);
                app.optionsDialog.appletOptionsContent = util.findElementById(app.ControlIDs.appletOptionsContent, appElements.contentDiv);
                var notificationsDiv = util.findElementById(app.ControlIDs.notificationsDiv, appElements.contentDiv);
                var appButtonsDiv = util.findElementById(app.ControlIDs.appButtonsDiv, appElements.contentDiv);
                app.noServerText = $("#noServerText");
                app.noServerLink = $("#addServerAnchor");
                app.noAppletLink = $($format("#{0} > a", app.ControlIDs.noAppletsSplashScreen));
                app._userHeader = util.findElementById(app.ControlIDs.userHeader, appletHeaderContainer);
                app.optionsButton = util.findElementById(app.ControlIDs.optionsButton, appletHeaderContainer);
                app._userHeaderText = util.findElementById(app.ControlIDs.userHeaderText, app._userHeader);
                app._userMenu = util.findElementById(app.ControlIDs.userMenu, appletHeaderContainer);
                app._signOutLink = $("a", app._userMenu);
                app._unauthorizedDescriptionSpan = util.findElementById(app.ControlIDs.unauthorizedDescriptionSpan, appElements.contentDiv);
                app._unauthorizedSignOutButton = util.findElementById(app.ControlIDs.unauthorizedSignOutButton, appElements.contentDiv);

                const appletDivFocus = (event) => {
                    const iframeElement = app.appletDiv.children().first() as JQuery<HTMLIFrameElement>;
                    if (iframeElement && iframeElement.length) {
                        appletInteraction.messageHandler.sendMessage((iframeElement[0]).contentWindow, window.location.origin, new appletMessaging.Requests.GetFocusRequest());
                    }

                    app.appletDiv.off("focusout", appletDivFocus);
                };

                // Configure a focus handler for the HTML element that ensures it doesn't receive focus
                const htmlElement = $("html").focusin(
                    (event) => {
                        if (event.target === htmlElement) {
                            // If the related target (where it tabbed from) is the first element, set focus to the last element; otherwise, set focus to the first element.
                            try {
                                util.setFocus($(document), { FocusLast: event.relatedTarget === $(":tabbable:not([aria-disabled='true'])")[0] });
                            } catch (e) { } // Swallow :tabbable exception - just don't set focus
                        } else if (event.target == app.appletDiv[0]) {
                            app.appletDiv.on("focusout", appletDivFocus);
                            app.appletDiv.blur();
                        }
                    }
                )[0];

                util.changeAttribute(app.optionsButton, util.AriaAttributes.Label, resources.dynamicsApp.optionsButtonLabel());
                util.customTooltip(app.optionsButton[0], resources.dynamicsApp.optionsButtonTooltip(), "tooltipText");

                $("#manualSignInAttemptedText").text(resources.authentication.manualSignInAttempted());
                var signOutText = resources.authentication.signOutLabel();
                app._signOutLink.text(signOutText).attr("title", signOutText);
                app._unauthorizedSignOutButton.text(signOutText).attr("title", signOutText);

                // This function is used to make the applet selection list shown or hidden
                var appletTitleContainer = app.appletTitle.parent();
                function toggleSelectionShown(shown: boolean): void {
                    util.changeAttribute(appletTitleContainer, appElements.ContentAttributes.appletSelectionShown, shown);
                }

                const UserMenuShownAttribute = "data-user-menu-shown";
                var menuExpanded = false;
                function toggleUserMenuShown(expand: boolean, refocus: boolean = false): void {
                    menuExpanded = expand;
                    var headerWidth = app._userHeader.width() - 2; // subtract the border
                    var menuWidth = app._userMenu.width();
                    if (expand && headerWidth > menuWidth) {
                        app._userMenu.width(headerWidth);
                    }
                    util.changeAttribute(appletHeaderContainer, UserMenuShownAttribute, expand);
                    util.changeAttribute(app._userHeader, util.AriaAttributes.Expanded, expand);
                    if (expand) {
                        util.setFocus(app._signOutLink);
                    } else if (refocus) {
                        util.setFocus(app._userHeader);
                    }
                }

                // Assign the sign in link's click handler
                app._signInLink.click(function signInLinkClicked(event: JQuery.ClickEvent<HTMLElement>) {
                    const authenticator = authentication.getAuthenticator();
                    if (authenticator) {
                        authenticator.requestSignIn();
                    }
                });

                app._userMenu.click((event) => {
                    event.stopPropagation();
                });

                app._userMenu.keydown((event) => {
                    switch (event.which) {
                        case $.ui.keyCode.ESCAPE: // Close the menu
                            toggleUserMenuShown(false, true);
                            event.stopPropagation();
                            break;
                        case $.ui.keyCode.TAB: // Prevent tabbing outside of the menu
                            event.preventDefault();
                            event.stopPropagation();
                            break;
                    }
                });

                util.customTooltip(_userHeader[0], resources.dynamicsApp.userButtonTooltip(), "tooltipText");

                app._userHeader.click((event) => {
                    toggleUserMenuShown(!menuExpanded);
                    event.stopPropagation();
                });

                // Bind a click handler for the sign out link that signs out the current user
                app._signOutLink.click(() => {
                    toggleUserMenuShown(false, true);
                    app.signOut();
                });
                app._unauthorizedSignOutButton.click(app.signOut);

                // Bind a click handler for the applet title that toggles the applet selection list when there are multiple ready applets
                app.appletTitle.click(function appletTitleClicked(event: JQuery.ClickEvent<HTMLElement>): void {

                    // Determine if the applet selection is current shown
                    var selectionShown = appletTitleContainer.attr(appElements.ContentAttributes.appletSelectionShown) === "true";

                    // Toggle the applet selection or make it hidden if there are not multiple ready applets
                    toggleSelectionShown(!selectionShown && appletInteraction.getAppletsReadyState() === appState.AppletsReadyState.Multiple);

                    // Prevent the event from propagating to the document
                    event.stopPropagation();
                });

                function documentClicked(event: JQuery.ClickEvent<HTMLElement|Document>): void {
                    toggleSelectionShown(false);
                    toggleUserMenuShown(false);
                }

                // Bind event handlers for document click and window blur events to hide the applet selection whenever a click is performed
                $(document).click(documentClicked);
                $(window).blur(<JQuery.EventHandler<any, null>>documentClicked);

                // Bind click handlers for "no server" and "no applet" links
                app.noServerLink.click(function noServerLinkClicked(event: JQuery.ClickEvent<HTMLElement>): void {
                    app.optionsDialog.open();
                    app.optionsDialog.dynamicsAppOptions.generalOptionsGroup.serverInput.select();
                });
                app.noAppletLink.click(function noAppletLinkClicked(event: JQuery.ClickEvent<HTMLElement>): void {
                    app.optionsDialog.open();
                    app.optionsDialog.configureAppletClicked(event);
                });

                // Get height of button div via code to enable the content pane to stretch to fill the entire pane.
                // The height of the footer can vary based on WriteFooterContent
                var topOfFooter = util.findElementById(app.ControlIDs.footerDiv).height();
                util.findElementById(app.ControlIDs.generalContentDiv).css('bottom', topOfFooter + 'px');

                app.initializeOptionsDialog();
                app.initializeAppletConfigDialog();

                // Populate localized strings
                app._copilotForFinanceBannerTitle.text(resources.dynamicsApp.copilotForFinanceBannerTitle());
                app._copilotForFinanceBannerLink.text(resources.dynamicsApp.copilotForFinanceBannerLinkText());
                app._copilotForFinanceBannerBodyText.text(resources.dynamicsApp.copilotForFinanceBannerBodyText());
                app._signInLink.text(resources.dynamicsApp.signInButtonLabel());
                util.changeAttribute(app._signInLink, "title", resources.dynamicsApp.signInButtonTitle());
                app.noServerText.text(resources.dynamicsApp.noServerMessage());
                app.noServerLink.text(resources.dynamicsApp.noServerLinkLabel());
                util.changeAttribute(app.noServerLink, "title", resources.dynamicsApp.noServerLinkTitle());
                app.noAppletLink.text(resources.dynamicsApp.noAppletLinkLabel());
                util.changeAttribute(app.noAppletLink, "title", resources.dynamicsApp.optionsAppletConfigButtonTitle());
                $("#noServerExternalLinks").text(resources.dynamicsApp.externalLinksHeading());
                $("#addinDetailsLink").text(resources.dynamicsApp.addinDetailsLinkLabel());
                $("#lcsLink").text(resources.dynamicsApp.lcsLinkLabel());

                // Create and initialize notifications handler
                notificationHandling.handler = new notificationHandling.NotificationsHandler(appButtonsDiv);
                notificationHandling.handler.initialize();

                // Create and initialize operation statuses handler
                operationHandling.handler = new operationHandling.OperationHandler(notificationsDiv);
                operationHandling.handler.initialize();

                // Create and initialize errors handler
                messageBoxHandling.handler = new messageBoxHandling.MessageBoxHandler();
                messageBoxHandling.handler.initialize();

                // Create and initialize dialog handler
                dialogs.handler = new dialogs.DialogHandler();
                dialogs.handler.initialize();

                function completeAppInitialization(): void {

                    // Indicate the app is ready
                    appElements.contentDiv.attr(appElements.ContentAttributes.appReady, "true");
                    app.updateUIFromState();

                    appState.State.IsInitialized = true;

                    // If the language could not be set to the desired language, notify the use that a different language is being used.  The working language is always in upper case from our call to resources.setLanguage.
                    desiredLanguage = desiredLanguage.toUpperCase();
                    if (loader.CurrentLocale != desiredLanguage) {
                        telemetry.events.desiredLanguageUnavailable.log(desiredLanguage, loader.CurrentLocale);
                    }

                    completeCallback();
                }

                if (appState.State.CurrentApplication !== undefined) {

                    var styleSheetNamePrefix = appState.State.CurrentApplication === util.ApplicationType.Word ? "Word" : "Excel";

                    let appCssName = app.getAppCssName(styleSheetNamePrefix);
                    // eslint-disable-next-line security/detect-non-literal-require -- variable is restricted to one of two possibilities
                    let themeCssName = require(`@ax/office-shared-lib/less/${styleSheetNamePrefix}Theme.less`);
                    util.addStyleSheet(themeCssName, function themeStyleSheetLoaded(): void {
                        util.addStyleSheet(appCssName, completeAppInitialization);
                    });
                } else {
                    completeAppInitialization();
                }
            });
    }

    /** Signs out the current user. */
    export function signOut(): void {
        const authenticator = authentication.getAuthenticator();
        if (authenticator) {
            app.unauthenticate(authenticator.signOut, appState.AuthenticationState.signingOut);
        }
    }

    /** Initializes the options dialog controls. */
    export function initializeOptionsDialog(): void {
        app.optionsDialog.heading = $('#optionsDialog .dialogHeader');
        app.optionsDialog.cancelButton = $('#optionsCancelButton');
        app.optionsDialog.okButton = $('#optionsOkButton');
        app.optionsDialog.dynamicsAppOptions.heading = $("#appOptionsHeader button");
        app.optionsDialog.dynamicsAppOptions.generalOptionsGroup.container = $('#appGeneralOptionsContainer');
        app.optionsDialog.dynamicsAppOptions.generalOptionsGroup.heading = $("#appGeneralOptionsGroupHeader");
        app.optionsDialog.dynamicsAppOptions.generalOptionsGroup.serverLabel = $('#serverLabel');
        app.optionsDialog.dynamicsAppOptions.generalOptionsGroup.serverInput = $('#serverInput');
        app.optionsDialog.dynamicsAppOptions.generalOptionsGroup.languageLabel = $('#languageLabel');
        app.optionsDialog.dynamicsAppOptions.generalOptionsGroup.languageInput = $('#languageInput');
        app.optionsDialog.dynamicsAppOptions.advancedOptionsGroup.heading = $("#appAppletOptionsGroupHeader");
        app.optionsDialog.dynamicsAppOptions.advancedOptionsGroup.appletConfigurationButton = $("#appletConfigurationButton");

        var telemetryOptionsContainer = $("#appTelemetryOptionsContainer");
        telemetryOptionsContainer.find("h3").text(resources.dynamicsApp.telemetryOptionsTitle());
        telemetryOptionsContainer.find("label").text(resources.dynamicsApp.optionsDialogCorrelationIdText());
        app.optionsDialog.dynamicsAppOptions.telemetryOptionsGroup.correlationIdInput = telemetryOptionsContainer.find("input");

        var advancedOptionsContainer = $("#appAdvancedOptionsContainer");
        advancedOptionsContainer.find("label").text(resources.dynamicsApp.optionsDialogVersionText());
        advancedOptionsContainer.find("input").val(window.dynamicsOfficeBuild.buildVersion);

        app.optionsDialog.heading.text(resources.dynamicsApp.optionsDialogTitle());
        app.optionsDialog.cancelButton.text(resources.dynamicsApp.optionsCancelButtonText());
        util.changeAttribute(app.optionsDialog.cancelButton, "title", resources.dynamicsApp.optionsCancelButtonTitle());
        app.optionsDialog.okButton.text(resources.dynamicsApp.optionsOkButtonText());
        util.changeAttribute(app.optionsDialog.okButton, "title", resources.dynamicsApp.optionsOkButtonTitle());
        app.optionsDialog.dynamicsAppOptions.heading.text(resources.dynamicsApp.dynamicsAppOptionsTitle());
        app.optionsDialog.dynamicsAppOptions.generalOptionsGroup.heading.text(resources.dynamicsApp.dynamicsAppGeneralOptionsTitle());
        app.optionsDialog.dynamicsAppOptions.generalOptionsGroup.serverLabel.text(resources.dynamicsApp.optionsDialogServerText());
        app.optionsDialog.dynamicsAppOptions.generalOptionsGroup.languageLabel.text(resources.dynamicsApp.optionsDialogLanguageText());
        app.optionsDialog.dynamicsAppOptions.advancedOptionsGroup.heading.text(resources.dynamicsApp.dynamicsAppAdvancedOptionsTitle());
        app.optionsDialog.dynamicsAppOptions.advancedOptionsGroup.appletConfigurationButton.text(resources.dynamicsApp.optionsAppletConfigButtonLabel());
        util.changeAttribute(app.optionsDialog.dynamicsAppOptions.advancedOptionsGroup.appletConfigurationButton, "title", resources.dynamicsApp.optionsAppletConfigButtonTitle());

        app.optionsButton.click(app.optionsDialog.open);
        app.optionsDialog.cancelButton.click(app.optionsDialog.close);
        app.optionsDialog.okButton.click(app.optionsDialog.okClicked);
        app.optionsDialog.dynamicsAppOptions.heading.click(app.toggleOptionSection);
        app.optionsDialog.dynamicsAppOptions.advancedOptionsGroup.appletConfigurationButton.click(app.optionsDialog.configureAppletClicked);

        var languages = Object.keys(LocaleInfo);
        languages.sort();
        $.each(languages, function(index, language) {
            let currentLocale = LocaleInfo[language];
            if (!isIndirectCulture(currentLocale)) {
                $("<option></option>", { value: language, text: currentLocale.englishName }).appendTo(app.optionsDialog.dynamicsAppOptions.generalOptionsGroup.languageInput);
            }
        });
    }

    /** Initializes the applet configuration dialog controls. */
    export function initializeAppletConfigDialog(): void {

        app.appletConfigurationDialog.heading = $($format("#{0} > h1", app.appletConfigurationDialog.dialogID));
        app.appletConfigurationDialog.configDialogContainer.heading = $("#appletConfigDialogContainer > h2");
        app.appletConfigurationDialog.configDialogContainer.configContent = $("#appletConfigContent");
        app.appletConfigurationDialog.okButton = $("#appletConfigOkButton");
        app.appletConfigurationDialog.cancelButton = $("#appletConfigCancelButton");

        app.appletConfigurationDialog.heading.text(resources.dynamicsApp.appletConfigDialogTitle());
        app.appletConfigurationDialog.configDialogContainer.heading.text(resources.dynamicsApp.appletConfigGroupTitle());
        app.appletConfigurationDialog.okButton.text(resources.dynamicsApp.appletConfigOkButtonLabel());
        util.changeAttribute(app.appletConfigurationDialog.okButton, "title", resources.dynamicsApp.appletConfigOkButtonTitle());
        app.appletConfigurationDialog.cancelButton.text(resources.dynamicsApp.appletConfigCancelButtonLabel());
        util.changeAttribute(app.appletConfigurationDialog.cancelButton, "title", resources.dynamicsApp.appletConfigCancelButtonTitle());

        app.appletConfigurationDialog.okButton.click(app.appletConfigurationDialog.okClicked);
        app.appletConfigurationDialog.cancelButton.click(app.appletConfigurationDialog.closeClicked);
    }

    /**
     * Loads and parses the configuration object from the document, applying default values as necessary.
     * If the document did not contain a configuration object one is created and set into the document.
     * @returns The configuration object.
     */
    export function loadConfiguration(): appState.AddinSettings {

        // Find the runtime configuration
        let runtimeConfig = <appState.AddinSettings>Office.context.document.settings.get(app.runtimeConfigKey);

        // If no config was found, just set an empty object to the document config.
        if (runtimeConfig === undefined || runtimeConfig === null) {
            Office.context.document.settings.set(app.runtimeConfigKey, {});
            runtimeConfig = Office.context.document.settings.get(app.runtimeConfigKey);
        }

        // Check all expected config keys and ensure a value is set for each one
        if (runtimeConfig.HostName === undefined) {
            runtimeConfig.HostName = "";
        } else {

            // If the last character of the host name is a trailing slash, remove it
            var hostNameLastIndex = runtimeConfig.HostName.length - 1;
            if (runtimeConfig.HostName.substr(hostNameLastIndex) === "/") {
                runtimeConfig.HostName = runtimeConfig.HostName.substr(0, hostNameLastIndex);
            }
        }

        if (runtimeConfig.Applets === undefined) {
            runtimeConfig.Applets = {};
        }

        if (runtimeConfig.ActiveAppletId === undefined) {
            runtimeConfig.ActiveAppletId = "";
        }

        if (runtimeConfig.Language === undefined) {
            runtimeConfig.Language = "";
        }

        if (!runtimeConfig.DocumentId) {
            runtimeConfig.DocumentId = util.generateGuid();
        }

        if (!runtimeConfig.DateCreated) {
            runtimeConfig.DateCreated = new Date().toISOString();
        }

        if (!runtimeConfig.GenerationActivityId) {
            runtimeConfig.GenerationActivityId = "";
        }

        return runtimeConfig;
    }

    /**
     * Loads and parses the features object from the document, applying default values as necessary.
     * If the document did not contain a features object one is created and set into the document.
     * @returns The features object.
     */
    export function loadFeatures(): util.AddinFeatures {

        // Find the runtime configuration
        let features = <util.AddinFeatures>Office.context.document.settings.get(app.featuresKey);

        // If no features were found, just set an empty object as the features.
        if (features === undefined || features === null) {
            Office.context.document.settings.set(app.featuresKey, {});
            features = Office.context.document.settings.get(app.featuresKey);
        }

        if (features.LogDomEvents === undefined) {
            features.LogDomEvents = false;
        }

        if (features.LogTelemetry === undefined) {
            features.LogTelemetry = true;
        }

        if (!features.LogDomEvents) {
            telemetry.events.noopDomEvents();
        }

        features.ResourceHeadersRefresh = true;

        return features;
    }

    /** Initializes interaction with applets and starts the authentication process. */
    export function onInitialized(): void {

        if (appState.State.CurrentApplication === undefined) {
            // Abort any further configuration if the current application is not recognized
            app.updateUIFromState();
            telemetry.errors.officeApplicationNotRecognized.show(resources.dynamicsApp.unrecognizedApplicationError());
        } else {
            // Initialize applet interaction
            appletInteraction.initialize();

            app.postInitialization();
        }
    }

    /** Performs post initialization work, beginning the authentication flow appropriately. */
    export function postInitialization(): void {

        // Verify authentication if a host name exists (TODO: verify the host name with threat mitigation work TFS#3393187)
        if (appState.State.Settings.HostName !== "") {
            app.startAuthentication();
        }
    }

    /** Starts authenticating the app. If the app is currently refreshing or publishing, the method waits until complete before authenticating. */
    export function startAuthentication(): void {
        var callbackHandler = new app._StartAuthenticationCallbackHandler();
        callbackHandler._executeAuthentication();
    }

    export function serverConfigRetrieved(success, options, errorDetails, authenticated) {
        if (appState.State.ServerConfig && !appState.State.ServerConfig?.RedirectUrl) {
            return;
        }

        options = options || {};

        if (success) {
            appState.State.ServerConfig = options.Data;

            // Disable telemetry if no key was provided or if a key that indicates telemetry should not be enabled is used
            if (
                (!("AppInsightsKey" in appState.State.ServerConfig)) ||
                (
                    !appState.State.ServerConfig.AppInsightsKey ||
                    (
                        typeof appState.State.ServerConfig.AppInsightsKey === "string" &&
                        (disabledInstrumentationKeys.indexOf(appState.State.ServerConfig.AppInsightsKey.trim()) !== -1)
                    )
                )
            ) {
                if (appState.State.Features === undefined) {
                    appState.State.Features = {};
                }
                appState.State.Features.LogTelemetry = false;
                window.appInsights.config.disableTelemetry = true;
                delete sessionStorage.AppInsightsKey;
            } else {
                telemetryLogging.initializeTelemetry(appState.State.ServerConfig.AppInsightsKey);
                sessionStorage.AppInsightsKey = window.appInsights.config.connectionString;
            }

            if (appState.State.ServerConfig.AuthType === appState.AuthType.OAuth && appState.State.Settings.Authentication && appState.State.Settings.Authentication.Tenant) {
                appState.State.ServerConfig.AuthTenantId = appState.State.Settings.Authentication.Tenant;
            }

            if (appState.State.Settings.Headers === undefined) {
                appState.State.Settings.Headers = {};
            }

            if (appState.State.ServerConfig.Headers === undefined) {
                appState.State.ServerConfig.Headers = {};
            }

            if (appState.State.ServerConfig.Features && appState.State.ServerConfig.Features.indexOf("EnableAppCorrelationIdHeader") >= 0) {
                // Set the correlation id for all calls from this excel add-in
                appState.State.ServerConfig.Headers["x-ms-correlation-id"] = { DisplayName: "x-ms-correlation-id", Value: appState.State.CorrelationID };
            }

            if (appState.State.ServerConfig.Features && appState.State.ServerConfig.Features.indexOf("EnableAppClientSessionHeader") >= 0) {
                appState.State.ServerConfig.Headers["x-ms-client-session-id"] = { DisplayName: "x-ms-client-session-id", Value: appState.State.CorrelationID, IsHidden: true };
            }

            if (appState.State.ServerConfig.LearnMoreUrl) {
                notificationHandling.handler.setLearnMoreLink(appState.State.ServerConfig.LearnMoreUrl);
            }

            var companyName, environmentName, header;
            for (header in appState.State.Settings.Headers) {
                var headerConfig = appState.State.ServerConfig.Headers[header];
                if (headerConfig !== undefined) {
                    headerConfig.Value = appState.State.Settings.Headers[header];
                    if (headerConfig.DisplayName === companyHeaderName) {
                        companyName = headerConfig.Value;
                    } else if (headerConfig.DisplayName === serverConfigEnvironmentDisplayName) {
                        environmentName = headerConfig.Value;
                    }
                }
            }

            if (!environmentName) {
                for (header in appState.State.ServerConfig.Headers) {
                    var serverConfigHeader = appState.State.ServerConfig.Headers[header];
                    if (serverConfigHeader !== undefined) {
                        if (serverConfigHeader.DisplayName === serverConfigEnvironmentDisplayName) {
                            if (!environmentName) {
                                environmentName = serverConfigHeader.Value;
                            }
                        }
                    }
                }
            }

            // Set the user's environment and company name from the server configuration and format the DataEndpoint Url.
            if (environmentName) {
                appState.State.ServerConfig.DataEndpointUrl = $format(appState.State.ServerConfig.DataEndpointUrl, environmentName);

                if (companyName) {
                    const companyNameElement = $("<span></span>", { text: resources.dynamicsApp.dynamicsAppCompanyText(companyName) });
                    app._userMenu.prepend(companyNameElement);
                    $("<br /><hr />").insertAfter(companyNameElement);
                }

                const environmentNameElement = $("<span></span>", { text: resources.dynamicsApp.dynamicsAppEnvironmentText(environmentName) });
                app._userMenu.prepend(environmentNameElement);
                $("<br />").insertAfter(environmentNameElement);
            }

            app.saveSettings(function headerSettingsSaved() {
                if (!authenticated) {
                    app.authenticate(app.authenticationComplete);

                    if (!appState?.State?.ServerConfig?.Features || appState.State.ServerConfig.Features.indexOf("EnhancedServerConfigFeature") < 0) {
                        telemetry.operations.retrievingConfiguration.end(resources.dynamicsApp.retrievingConfigurationSucceededMessage());
                    }
                } else {
                    app.loadRegistrations();
                }
            });
        } else {
            errorDetails[options.ErrorSource] = options.ErrorTelemetryDetails || options.ErrorDetails;
            if (_StartAuthenticationCallbackHandler.ServerConfigLocations.Resource in errorDetails && _StartAuthenticationCallbackHandler.ServerConfigLocations.Service in errorDetails) {
                var errorOptions: any = {
                    LinkOptions: {
                        Text: resources.common.retry(),
                        Action: app.startAuthentication
                    }
                };
                if (options.ErrorDetails !== undefined) {
                    errorOptions.Details = options.ErrorDetails;
                }
                telemetry.errors.configurationRetrievalFailed.show(resources.dynamicsApp.configRetrievalErrorMessage(), errorOptions);
                telemetry.errors.configurationServiceFailed.add(
                    resources.dynamicsApp.serviceFailedToRetrieveConfigNotification(errorDetails[_StartAuthenticationCallbackHandler.ServerConfigLocations.Service].TextStatus, errorDetails[_StartAuthenticationCallbackHandler.ServerConfigLocations.Service].ErrorThrown));
                telemetry.errors.configurationResourceFailed.add(resources.dynamicsApp.resourceFailedToRetrieveConfigNotification(errorDetails[_StartAuthenticationCallbackHandler.ServerConfigLocations.Resource]));
                telemetry.operations.retrievingConfiguration.end(resources.dynamicsApp.retrievingConfigurationFailedMessage());
            }
        }
    }

    export function enhancedServerConfigRetrieved(success, options) {
        if (success && appState.State.ServerConfig) {

            EnhancedConfigResponseHandler.handleResponse(options);

            if (!appState.State.HasEnhancedConfigBeenRetrieved) {
                appState.State.HasEnhancedConfigBeenRetrieved = true;
                telemetry.operations.retrievingConfiguration.end(resources.dynamicsApp.retrievingConfigurationSucceededMessage());

                app.saveSettings(function headerSettingsSaved() {
                    app.loadRegistrations();
                });
            }
        } else {
            var errorOptions: any = {
                LinkOptions: {
                    Text: resources.common.retry(),
                    Action: app.startAuthentication
                }
            };

            if (options.ErrorDetails !== undefined) {
                errorOptions.Details = options.ErrorDetails;
            }

            telemetry.errors.configurationRetrievalFailed.show(resources.dynamicsApp.enhancedConfigRetrievalErrorMessage(), errorOptions);
            telemetry.operations.retrievingConfiguration.end(resources.dynamicsApp.retrievingEnhancedConfigurationFailedMessage());
        }
    }

    /** Handles callbacks and internal logic for the startAuthentication() method. */
    export class _StartAuthenticationCallbackHandler {

        /* jshint -W040 */
        constructor() {
            this._executeAuthentication = this._executeAuthentication.bind(this);
        }

        /* jshint +W040 */

        public static ServerConfigLocations = {
            Service: "Service",
            Resource: "Resource"
        }

        /** Executes the actual authentication process. */
        public _executeAuthentication(): void {
            appState.State.AuthenticationState = appState.AuthenticationState.authenticating;
            app.updateUIFromState();
            app._executeConfiguration();
        }
    }

    /**
    * Executes the configuration retrieval process.
    * @param features Optional string array of features to merge with those specified in the server configuration.
    */
    export function _executeConfiguration(features?: Array<string>): void {
        telemetry.operations.retrievingConfiguration.begin(resources.dynamicsApp.retrievingConfigurationMessage());
        appState.State.ServerConfig = undefined;
        var errorDetails: any = {};

        let retrievalSuccessful = false;
        const handler = new JQueryThrottlingHandler(
            () => {
                telemetry.events.odataCallAttempted.log("", "ServerConfiguration", "N/A");
                $.ajax({
                    dataType: "json",
                    jsonp: false,
                    url: $format("{0}/OfficeApps/Public/ServerConfiguration", appState.State.Settings.HostName),
                    method: "GET",
                    cache: false,
                    timeout: 30000,
                    success: function(data: appState.ServerConfiguration, textStatus, jqXHR) {
                        if (!retrievalSuccessful) {
                            retrievalSuccessful = true;
                            if (features) {
                                data.Features = (data.Features || []).concat(features || []);
                            }
                            app.serverConfigRetrieved(true, { Data: data }, errorDetails, false);
                        }
                    },
                    error: function(jqXHR, textStatus, errorThrown) {
                        if (!retrievalSuccessful && !handler.handleThrottleResponse(jqXHR)) {
                            app.serverConfigRetrieved(false, { ErrorSource: _StartAuthenticationCallbackHandler.ServerConfigLocations.Service, ErrorDetails: textStatus || errorThrown, ErrorTelemetryDetails: { TextStatus: textStatus, ErrorThrown: errorThrown } }, errorDetails, false);
                        }
                    }
                });
            },
            (activityId: string, waitTime: string, retryAttempt: number, retrying: boolean): void => {
                telemetry.events.odataCallThrottled.log("", "ServerConfiguration", "N/A", "N/A", waitTime, retryAttempt, retrying);
            }
        );
        handler.send();

        var requestMessage = new messaging.Requests.GetConfigRequest();

        appletInteraction.sendAppResourceRequest(
            appletInteraction.WellKnownResources.Config.ID,
            requestMessage,
            function onConfigRetrieved(success: boolean, responseMessage: messaging.Responses.GetConfigResponse) {
                if (!retrievalSuccessful) {
                    if (success) {
                        if (responseMessage.Status === messaging.ResponseStatus.success) {
                            retrievalSuccessful = true;
                            app.serverConfigRetrieved(true, { Data: responseMessage.Config }, errorDetails, false);
                        } else {
                            app.serverConfigRetrieved(false, { ErrorSource: _StartAuthenticationCallbackHandler.ServerConfigLocations.Resource, ErrorDetails: responseMessage.FailureReason }, errorDetails, false);
                        }
                    } else {
                        app.serverConfigRetrieved(false, { ErrorSource: _StartAuthenticationCallbackHandler.ServerConfigLocations.Resource, ErrorDetails: resources.dynamicsApp.resourceFailedToLoadError() }, errorDetails, false);
                    }
                }
            }
        );
    }

    /**
     * Upon successful authentication, starts the applet configuration process.
     * @param authenticated Indicates whether or not the app was authenticated.
     */
    export function authenticationComplete(authenticated: boolean): void {
        if (authenticated) {
            if (appState.State?.AuthContext && appState.State?.ServerConfig.RedirectUrl) {

                appState.State.AuthContext.acquireToken(appState.State.ServerConfig.AuthResourceId).then((accessToken: string) => {
                    if (accessToken) {
                        var errorDetails: any = {};
                        let headers: util.PropertyBag<string> = { Authorization: "Bearer " + accessToken, "x-ms-correlation-id": appState.State.CorrelationID };

                        if (appState.State.Settings.Headers[environmentHeaderName]) {
                            appState.State.ServerConfig.RedirectUrl += "?environmentName=" + (appState.State.Settings.Headers[environmentHeaderName]);
                        }

                        if (appState.State.ServerConfig?.Features && appState.State.ServerConfig.Features.indexOf("EnhancedServerConfigFeature") >= 0) {
                            var requestMessage: messaging.Requests.GetEnhancedConfigRequest = new messaging.Requests.GetEnhancedConfigRequest();
                            requestMessage.EnhancedConfigUrl = appState.State.ServerConfig.RedirectUrl;
                            requestMessage.Headers = headers;

                            appletInteraction.sendAppResourceRequest(
                                appletInteraction.WellKnownResources.EnhancedConfig.ID,
                                requestMessage,
                                function onConfigRetrieved(success: boolean, responseMessage: messaging.Responses.GetEnhancedConfigResponse) {
                                    if (success) {
                                        if (responseMessage.Status === messaging.ResponseStatus.success) {
                                            app.enhancedServerConfigRetrieved(true, { Data: responseMessage.Config });
                                        } else {
                                            app.enhancedServerConfigRetrieved(false, { ErrorSource: _StartAuthenticationCallbackHandler.ServerConfigLocations.Resource, ErrorDetails: responseMessage.FailureReason });
                                        }
                                    } else {
                                        app.enhancedServerConfigRetrieved(false, { ErrorSource: _StartAuthenticationCallbackHandler.ServerConfigLocations.Resource, ErrorDetails: resources.dynamicsApp.resourceFailedToLoadError() });
                                    }
                                }
                            );
                        } else {
                            const handler = new JQueryThrottlingHandler(
                                () => {
                                    telemetry.events.odataCallAttempted.log("", "EnhancedServerConfiguration", "N/A");
                                    $.ajax({
                                        dataType: "json",
                                        jsonp: false,
                                        url: appState.State.ServerConfig.RedirectUrl,
                                        method: "GET",
                                        cache: false,
                                        headers: headers,
                                        timeout: 30000,
                                        success: function(data) {
                                            app.serverConfigRetrieved(true, { Data: data }, errorDetails, true);
                                        },
                                        error: function(jqXHR, textStatus, errorThrown) {
                                            if (!handler.handleThrottleResponse(jqXHR)) {
                                                if (jqXHR?.responseJSON?.error?.message) {
                                                    textStatus = jqXHR.responseJSON.error.message;
                                                }
                                                errorDetails[_StartAuthenticationCallbackHandler.ServerConfigLocations.Resource] = { ErrorDetails: textStatus || errorThrown };
                                                app.serverConfigRetrieved(false, { ErrorSource: _StartAuthenticationCallbackHandler.ServerConfigLocations.Service, ErrorDetails: textStatus || errorThrown, ErrorTelemetryDetails: { TextStatus: textStatus, ErrorThrown: errorThrown } }, errorDetails, true);
                                            }
                                        }
                                    });
                                },
                                (activityId: string, waitTime: string, retryAttempt: number, retrying: boolean): void => {
                                    telemetry.events.odataCallThrottled.log("", "EnhancedServerConfiguration", "N/A", "N/A", waitTime, retryAttempt, retrying);
                                }
                            );
                            handler.send();
                        }
                    } else {
                        var errorDetails: any = {};
                        errorDetails[_StartAuthenticationCallbackHandler.ServerConfigLocations.Resource] = resources.appletInteraction.authenticationError();
                        app.serverConfigRetrieved(false, { ErrorSource: _StartAuthenticationCallbackHandler.ServerConfigLocations.Service, ErrorDetails: resources.appletInteraction.authenticationError(), ErrorTelemetryDetails: { TextStatus: resources.appletInteraction.authenticationError(), ErrorThrown: resources.appletInteraction.authenticationError() } }, errorDetails, true);
                    }
                }, (error: AuthError) => {
                    var errorDetails: any = {};
                    errorDetails[_StartAuthenticationCallbackHandler.ServerConfigLocations.Resource] = error.errorMessage || resources.appletInteraction.authenticationError();
                    app.serverConfigRetrieved(false, { ErrorSource: _StartAuthenticationCallbackHandler.ServerConfigLocations.Service, ErrorDetails: resources.appletInteraction.authenticationError(), ErrorTelemetryDetails: { TextStatus: resources.appletInteraction.authenticationError(), ErrorThrown: resources.appletInteraction.authenticationError() } }, errorDetails, true);
                });
            } else {
                app.loadRegistrations();
            }
        }
        else if (appState.State?.ServerConfig?.RedirectUrl) {
            // If the app is not authenticated, but needs to be to perform the redirect call,
            // do not continue to try to retrieve the server configuration.
            telemetry.operations.retrievingConfiguration.end("");
        }
    }

    /**
     * Starts the registration retrieval process.
     */
    export function loadRegistrations(): void {
        telemetry.operations.registrationsRetrieval.begin(resources.dynamicsApp.registrationsRetrievalMessage());
        app.generateHeaderOptionsDOM();
        appletInteraction.retrieveRegistrations(app.registrationsRetrieved);
    }

    /**
     * Upon successful retrieval of applet and resource registration information, loads all registered applets. If retrieval failed an error is shown.
     * @param success Indicates whether or not registrations were retrieved.
     * @param notAuthorized Indicates if the failure was due to a lack of authorization.
     */
    export function registrationsRetrieved(success: boolean, notAuthorized: boolean): void {

        // Adjust state to indicate if registrations were retrieved
        appState.State.RegistrationsRetrieved = success;

        if (success) {
            app.loadApplets();
        } else if (notAuthorized) {
            telemetry.errors.notAuthorized.show(
                resources.dynamicsApp.registrationRetrievalError(),
                {
                    Details: resources.dynamicsApp.notAuthorizedError(),
                    LinkOptions: { Text: resources.authentication.signOutLabel(), Action: app.signOut },
                });
            appState.State.AuthorizationState = appState.AuthorizationState.notAuthorized;
        } else {
            telemetry.errors.registrationRetrievalFailed.show(resources.dynamicsApp.registrationRetrievalError());
        }

        // End the registrations retrieval operation and update the UI from state
        telemetry.operations.registrationsRetrieval.end(success ? resources.dynamicsApp.registrationsRetrievalSucceededMessage() : resources.dynamicsApp.registrationsRetrievalFailedMessage());
        app.updateUIFromState();
    }

    /** Loads the registered applets applets that support the current application and activates the applet set as active in the file settings if possible. */
    export function loadApplets(): void {
        var appletsLoaded = 0;
        var appletsSupportingCurrentApplication = [];

        $.each(appletInteraction.registeredApplets, function(index, applet) {
            // Only load an applet if it supports the current application and there is a settings entry for it
            var supportsCurrentApplication = appState.State.CurrentApplication & applet.SupportedApplications;

            if (supportsCurrentApplication) {
                appletsSupportingCurrentApplication.push(applet);

                if (applet.AppletID in appState.State.Settings.Applets) {
                    appletInteraction.loadApplet(applet.AppletID, applet.Path, appState.State.Settings.Applets[applet.AppletID]);
                    appletsLoaded++;
                }
            }
        });

        // If no applets were loaded and there is only one registered applet that supports the current application, load it
        if (appletsLoaded === 0 && appletsSupportingCurrentApplication.length === 1) {
            var applet = appletsSupportingCurrentApplication[0];
            appletInteraction.loadApplet(applet.AppletID, applet.Path, undefined);
        }
    }

    /** Generates the header options DOM for the retrieved server configuration. */
    export function generateHeaderOptionsDOM(): void {

        // Add the header options after the language input.
        var lastAppOption = app.optionsDialog.dynamicsAppOptions.generalOptionsGroup.languageInput;

        $.each(appState.State.ServerConfig.Headers, (header: string, headerConfig: appState.ConfigurableHeaderDetails) => {
            if (headerConfig.IsHidden) {
                // Skip the header if it should not be displayed
                return;
            }

            let isSelect = $.isArray(headerConfig.ValidValues);
            let hasLabels = $.isArray(headerConfig.ValidLabels) && isSelect && headerConfig.ValidLabels.length === headerConfig.ValidValues.length;
            var headerOptionControl = $(isSelect ? "<select></select>" : "<input></input>", { "class": "stringOptionInput" });
            if (isSelect) {
                headerOptionControl.append($("<option></option>", { value: "", label: "" }));
                headerConfig.ValidValues.forEach((value, index, array) => {
                    headerOptionControl.append($("<option></option>", { value: value, label: hasLabels ? headerConfig.ValidLabels[index] : value }));
                });

                // If the current header value does not exist as a value but does exist as a label, then use the value associated to that label instead
                // This is to help changing from values to labels, since excel files will still refer to the old value until updated
                if (hasLabels && headerConfig.ValidValues.indexOf(headerConfig.Value) < 0) {
                    let labelIndex = headerConfig.ValidLabels.indexOf(headerConfig.Value);
                    if (labelIndex >= 0) {
                        headerConfig.Value = headerConfig.ValidValues[labelIndex];
                    }
                }
            }
            headerOptionControl.val(headerConfig.Value);
            $("<div></div>")
                .attr(app.DataHeaderOptionConstants.DataHeaderOptionsAttribute, header)
                .append($("<span></span>", { text: headerConfig.DisplayName }))
                .append(headerOptionControl)
                .insertAfter(lastAppOption);
        });
    }

    const headerOptionInputSelector = "input, select";

    /** Updates the header options DOM based on the current server configuration. */
    export function updateHeaderOptionsDOM(): void {
        var headerOptions = app.optionsDialog.dynamicsAppOptions.generalOptionsGroup.container.find(app.DataHeaderOptionConstants.DataHeaderOptionsSelector);

        $.each(headerOptions, function(headerIndex, headerOption) {
            var $headerOption = $(headerOption);
            var input = $headerOption.find(headerOptionInputSelector);
            var header = $headerOption.attr(app.DataHeaderOptionConstants.DataHeaderOptionsAttribute);
            var headerConfig = appState.State.ServerConfig.Headers[header];
            input.val(headerConfig.Value);
        });
    }

    /** The callbacks to be performed when the UI is updated. */
    export const updateUIFromStateCallbacks: Array<(state: appState.ApplicationState<appState.AddinSettings>) => void> = [];

    /** Updates the UI from the application state. */
    export function updateUIFromState(state: appState.ApplicationState<appState.AddinSettings> = appState.State): void {

        // Determine if there are applets loading
        var appletsLoading = state.AppletsLoadingCount > 0;

        // Apply the current state of initialization
        appElements.changeContentAttribute(appElements.ContentAttributes.Initialized, state.IsInitialized);

        // Apply whether or not manual sign in has been attempted
        appElements.changeContentAttribute(appElements.ContentAttributes.ManualSignInAttempted, state.ManualSignInAttempted);

        // Apply the current state of authentication
        appElements.changeContentAttribute(appElements.ContentAttributes.AuthenticationState, state.AuthenticationState);

        // Apply the current state of authorization
        appElements.changeContentAttribute(appElements.ContentAttributes.AuthorizationState, state.AuthorizationState);

        const authenticator = authentication.getAuthenticator();
        // Note: we force false for falsey values to ensure we pass actual booleans to further methods
        const authenticatorValid = (!!authenticator) && authenticator.isValid();
        let enableSignInLink = authenticatorValid;
        let enableSignOutControls = authenticatorValid;
        switch (state.AuthenticationState) {
            case appState.AuthenticationState.waitingUserAuthentication:
            case appState.AuthenticationState.authenticating:
            case appState.AuthenticationState.signingOut:
                enableSignOutControls = false;
            /* falls through */
            case appState.AuthenticationState.authenticated:
                enableSignInLink = false;
                break;
        }

        util.enableControl(app._signInLink, enableSignInLink);
        util.enableControl(app._signOutLink, enableSignOutControls);
        util.enableControl(app._unauthorizedSignOutButton, enableSignOutControls);

        // Apply the registrations retrieved state
        appElements.changeContentAttribute(appElements.ContentAttributes.registrationsRetrieved, state.RegistrationsRetrieved);

        // Apply the current applets ready state
        appElements.changeContentAttribute(appElements.ContentAttributes.appletsReadyState, state.AppletsReadyState);

        // Apply the applets loading state
        appElements.changeContentAttribute(appElements.ContentAttributes.appletsLoading, appletsLoading);

        // Enable or disable the applet configuration dialog related controls, based on whether registered applets have been retrieved and
        // the number of applets that are currently loading
        var appletConfigurationDialogAccessible = state.RegistrationsRetrieved && !appletsLoading;
        util.enableControl(app.optionsDialog.dynamicsAppOptions.advancedOptionsGroup.appletConfigurationButton, appletConfigurationDialogAccessible);
        util.enableControl(app.noAppletLink, appletConfigurationDialogAccessible);

        telemetry.events.appStateUpdated.log(
            state.AuthenticationState,
            state.AuthorizationState,
            state.AppletsReadyState,
            state.RegistrationsRetrieved,
            state.AppletsLoadingCount,
            (state.Features || {} as util.AddinFeatures).LogDomEvents,
            (state.Features || {} as util.AddinFeatures).LogTelemetry,
            state.ManualSignInAttempted);

        updateUIFromStateCallbacks.forEach((callback, index, array) => { callback(state); });
    }

    export interface AuthenticationCompleteCallback { (authenticated: boolean): void }

    /**
     * Common initialization function (to be called from each page).
     * @param authenticationCompleteCallback The callback function to perform when authentication is complete.
     */
    export function authenticate(authenticationCompleteCallback: AuthenticationCompleteCallback): void {
        var callbackHandler = new app.AuthenticateCallbackHandler(authenticationCompleteCallback, appState.State);

        // Initialize the authenticator
        const authenticator = authentication.getAuthenticator({
            authenticatingCallback: callbackHandler.authenticatingCallback,
            notAuthenticatedCallback: callbackHandler.notAuthenticatedCallback,
            signInCallback: callbackHandler.waitingUserAuthenticationCallback,
            authenticatedCallback: callbackHandler.authenticatedCallback
        });

        app._signInDescriptionSpan.text(resources.authentication.signInAsFormat(appState.State.Settings.HostName));

        // Check if the app is authenticated
        authenticator.checkAuthentication(true);
    }

    /** Handles the callbacks for authentication. */
    export class AuthenticateCallbackHandler {

        /** The callback to call when checking authentication is complete. */
        private authenticationCompleteCallback: AuthenticationCompleteCallback

        /** The state of the application being authenticated. */
        private state: appState.ApplicationState<appState.AddinSettings>;

        /**
         * @param authenticationCompleteCallback The callback to call when checking authentication is complete.
         * @param state The state of the application being authenticated.
         */
        constructor(authenticationCompleteCallback: AuthenticationCompleteCallback, state: appState.ApplicationState<appState.AddinSettings>) {
            this.authenticationCompleteCallback = authenticationCompleteCallback;
            this.state = state;
            this.authenticatingCallback = this.authenticatingCallback.bind(this);
            this.waitingUserAuthenticationCallback = this.waitingUserAuthenticationCallback.bind(this);
            this.authenticatedCallback = this.authenticatedCallback.bind(this);
            this.notAuthenticatedCallback = this.notAuthenticatedCallback.bind(this);
        }

        /** The callback for when the app is authenticating. */
        public authenticatingCallback(): void {
            this.state.AuthenticationState = appState.AuthenticationState.authenticating;
            app.updateUIFromState(this.state);
            telemetry.operations.authentication.begin(resources.dynamicsApp.authentication_Verifying());
        }

        /** The callback for when the app is waiting for the user to authenticate. */
        public waitingUserAuthenticationCallback(): void {
            this.state.AuthenticationState = appState.AuthenticationState.waitingUserAuthentication;
            app.updateUIFromState(this.state);
        }

        /**
         * The callback for when the app is authenticated.
         * @param originSet A property bag of valid origins, with origin as the key and value.
         * @param user Details about the authenticated user.
         */
        public authenticatedCallback(originSet: any, user?: AccountInfo): void {

            // Set the app's valid origin sets
            this.state.ValidOriginSet = {};
            for (var origin in originSet) {
                this.state.ValidOriginSet[origin.toUpperCase()] = origin;
            }
            this.state.ValidOriginSet[window.location.origin.toUpperCase()] = window.location.origin;

            if (user !== undefined) {
                const userDetails = appState.extractUserDetails(user);
                util.findElement(" > :not(a)", app._userMenu).remove();
                if (userDetails.objectID || userDetails.tenantID) {
                    telemetry.events.userCorrelation.log(userDetails.tenantID, userDetails.objectID);
                }

                // Set the user's display name as the text and title
                app._userHeaderText.text(userDetails.displayName);
                util.customTooltip(_userHeader[0], userDetails.displayName, "tooltipText");

                var userNameElement = $("<span></span>", { text: userDetails.userName });
                app._userMenu.prepend(userNameElement);
                $("<br /><hr />").insertAfter(userNameElement);

                app._unauthorizedDescriptionSpan.text(resources.dynamicsApp.notAuthorizedDescriptionFormat(resources.dynamicsApp.notAuthorizedError(), userDetails.displayName, userDetails.userName, this.state.Settings.HostName));
            }

            // Run common authentication complete logic
            this._authenticationComplete(true);
        }

        /** The callback for when the app is not authenticated. */
        public notAuthenticatedCallback(): void {

            this._authenticationComplete(false);
        }

        /**
         * The callback for when verifying the app authentication is complete, using the provided authenticated indicator to determine if the app is authenticated or not.
         * @param authenticated An indicator used to determine if the app is authenticated or not.
         */
        public _authenticationComplete(authenticated: boolean): void {

            // Set the app's authentication state appropriately and update the UI
            this.state.AuthenticationState = authenticated ? appState.AuthenticationState.authenticated : appState.AuthenticationState.notAuthenticated;
            app.updateUIFromState(this.state);

            // End the authentication operation, remove the message handler, and remove the sign in link click handler
            telemetry.operations.authentication.end(authenticated ? resources.dynamicsApp.authentication_Authenticated() : resources.dynamicsApp.authentication_NotAuthenticated());

            // Calls the provided authentication complete callback
            this.authenticationCompleteCallback(authenticated);
        }
    }

    /** The regular expression to use to determine if a URL is a valid HTTPS URL. */
    export const HttpsUrlExpression = /^https:\/\/[^ \"]+$/g;

    /** The result of determining what settings keys changed. */
    interface ChangedSettingKeysResult {

        /** The app settings keys that changed. */
        ChangedAppSettings: string[];

        /** The qualified applet settings keys that changed. */
        ChangedAppletSettings: string[];
    }

    /**
     * Determines what settings keys have changed. This code assumes that settings are never deleted from the new settings, only cleared or changed.
     * @param newSettings The new settings that are not yet saved.
     * @returns The result of the setting key changes determination.
     */
    export function _determineChangedSettingKeys(newSettings: appState.AddinSettings): ChangedSettingKeysResult {
        const result: ChangedSettingKeysResult = {
            ChangedAppSettings: [],
            ChangedAppletSettings: []
        };

        if (newSettings) {
            for (const setting of Object.getOwnPropertyNames(newSettings)) {
                let settingValue = newSettings[setting];
                if (setting === "Applets") {
                    for (const appletID in newSettings.Applets) {
                        settingValue = newSettings.Applets[appletID];
                        if (settingValue && typeof settingValue === "object") {
                            app._determineChangedAppletSettingKeys(appletID, settingValue, (appState.State.Settings || { Applets: undefined }).Applets[appletID], result.ChangedAppletSettings);
                        }
                    }
                } else if (typeof settingValue !== "object" && settingValue !== (appState.State.Settings || {})[setting]) {
                    result.ChangedAppSettings.push(setting);
                }
            }
        }

        return result;
    }

    /**
     * Determines what applets setting keys have changed. This code assumes that settings are never deleted from the new settings, only cleared or changed.
     * @param qualification The qualification to apply to the setting key (e.g. at least the applet ID)
     * @param newSettings The new applet settings that are not yet saved.
     * @param settings The current applet settings that are currently saved.
     * @param changedAppletSettings The array of changed applet setting keys.
     */
    export function _determineChangedAppletSettingKeys(qualification: string, newSettings: util.PropertyBag<any>, settings: util.PropertyBag<any>, changedAppletSettings: string[]): void {
        if (newSettings) {
            settings = settings || {};
            for (const setting of Object.getOwnPropertyNames(newSettings)) {
                var newSettingValue = newSettings[setting];
                var currentSettingValue = settings[setting];
                if (newSettingValue === currentSettingValue || ((newSettingValue === null || newSettingValue === undefined) && (currentSettingValue === null || currentSettingValue === undefined))) {
                    continue;
                } else if (newSettingValue === null || newSettingValue === undefined) {
                    changedAppletSettings.push(`${qualification}.${setting}`);
                } else if (Array.isArray(newSettingValue)) {
                    // Do a comparison of arrays but don't qualify beyond array member
                    if (JSON.stringify(currentSettingValue) !== JSON.stringify(newSettingValue)) {
                        changedAppletSettings.push(`${qualification}.${setting}`);
                    }
                } else if (typeof newSettingValue === "object") {
                    app._determineChangedAppletSettingKeys(`${qualification}.${setting}`, newSettingValue, currentSettingValue, changedAppletSettings);
                } else if (newSettingValue !== currentSettingValue) {
                    changedAppletSettings.push(`${qualification}.${setting}`);
                }
            }
        }
    }

    /**
     * Determines which options were changed, validating the changes before saving them.
     * Sets and saves the runtime config object on Office.context.document.settings.
     */
    export function updateSettings(): void {
        var newSettings = $.extend(true, {}, appState.State.Settings) as appState.AddinSettings;
        var appReloadRequired = false;

        function updateAppletOptions(shouldContinue: boolean): void {
            if (shouldContinue) {
                appletInteraction.updateAllAppletsOptions(newSettings, saveNewSettings);
            } else {
                completeUpdatingOptions(false);
            }
        }

        function saveNewSettings(): void {

            // Determine what settings changed, for telemetry reporting
            const changedSettingsResult = app._determineChangedSettingKeys(newSettings);

            // Merge the new settings back into the app's existing settings.
            $.extend(true, appState.State.Settings, newSettings);

            // Save the settings back into the document.
            app.saveSettings(function settingsSaved(asyncResult) {
                if (asyncResult.status === Office.AsyncResultStatus.Succeeded) {
                    completeUpdatingOptions(true);
                } else {
                    completeUpdatingOptions(false);
                }
            });

            // Log Telemetry
            telemetry.events.optionsSavedTelemetryEvent.log(changedSettingsResult.ChangedAppSettings, changedSettingsResult.ChangedAppletSettings);
        }

        function completeUpdatingOptions(updateAppState: boolean): void {
            if (updateAppState) {
                if (appReloadRequired) {

                    // If the add-in needs to be reloaded, just reload the app and it will pick up all the new settings.
                    window.location.reload();
                } else {
                    // Update the app's Server Config
                    if (appState.State.ServerConfig?.Headers) {
                        var header;
                        for (header in newSettings.Headers) {
                            var headerConfig = appState.State.ServerConfig.Headers[header];
                            if (headerConfig !== undefined) {
                                headerConfig.Value = newSettings.Headers[header];
                            }
                        }
                    }
                    dialogs.handler.closeActiveDialog(app.optionsDialog.optionsDialogId);
                }
            }
        }

        // Determine changes to the header settings.
        var headerOptions = app.optionsDialog.dynamicsAppOptions.generalOptionsGroup.container.find(app.DataHeaderOptionConstants.DataHeaderOptionsSelector);
        $.each(headerOptions, function(headerIndex, headerOption) {
            var $headerOption = $(headerOption);
            var input = $headerOption.find(headerOptionInputSelector);
            var header = $headerOption.attr(app.DataHeaderOptionConstants.DataHeaderOptionsAttribute);
            newSettings.Headers[header] = input.val() as string;
        });

        if (appState.State.ServerConfig?.Headers) {
            for (let header in newSettings.Headers) {
                var headerConfig = appState.State.ServerConfig.Headers[header];
                if (headerConfig !== undefined) {
                    if ((headerConfig.DisplayName === serverConfigEnvironmentDisplayName || headerConfig.DisplayName === companyHeaderName) && (headerConfig.Value !== newSettings.Headers[header])) {
                        appReloadRequired = true;
                    }
                }
            }
        }

        // Determine changes to app-wide settings.
        const serverInputResults = app.setConnection(app.optionsDialog.dynamicsAppOptions.generalOptionsGroup.serverInput.val() as string, newSettings);
        newSettings.Language = app.optionsDialog.dynamicsAppOptions.generalOptionsGroup.languageInput.val() as string;

        // Validate the app-wide setting changes.
        var appOptionsAreValid = serverInputResults.AppOptionsAreValid;
        appReloadRequired = appReloadRequired || serverInputResults.AppReloadRequired;

        if ((newSettings.Language || "").toUpperCase() !== (appState.State.Settings.Language || "").toUpperCase()) {
            appReloadRequired = true;
        }

        if (appOptionsAreValid) {
            if (appReloadRequired) {
                notificationHandling.showConfirmationDialog(
                    resources.dynamicsApp.optionsConfirmChangesTitle(),
                    updateAppletOptions,
                    {
                        Details: resources.dynamicsApp.optionsConfirmAppReloadMessage()
                    });
            } else {
                updateAppletOptions(true);
            }
        } else {
            completeUpdatingOptions(false);
        }
    }

    /** Represents the results of setting a settings option. */
    export interface SetSettingsOptionResults {

        /** Indicates if the app needs to be reloaded. */
        AppReloadRequired: boolean;

        /** Indicates if the options are valid. */
        AppOptionsAreValid: boolean;
    }

    interface SetConnectionOptions {

        /** Indicates if an empty connection should be allowed; defaults to true. */
        AllowEmptyConnection?: boolean;
    }

    /**
     * Attempts to set the connection settings option into the provided settings; if the connection is invalid, an error is displayed.
     * @param connection The connection to set.
     * @param settings The settings into which the connection should be set.
     * @param options The options that control how the connection setting is handled.
     * @returns The results of the set attempt.
     */
    export function setConnection(connection: string, settings: appState.AddinSettings, options: SetConnectionOptions = {}): SetSettingsOptionResults {

        const results: SetSettingsOptionResults = {
            AppReloadRequired: false,
            AppOptionsAreValid: true
        };

        settings.HostName = connection;
        if (!settings.HostName && options.AllowEmptyConnection === false) {
            telemetry.errors.invalidHostUrlEntered.show(resources.dynamicsApp.invalidHostUrlEnteredErrorMessage());
            results.AppOptionsAreValid = false;
        } else if (settings.HostName.toUpperCase() !== (appState.State.Settings.HostName || "").toUpperCase()) {
            results.AppReloadRequired = true;
            if (settings.HostName) {
                if (settings.HostName.search(HttpsUrlExpression) === -1) {
                    telemetry.errors.invalidHostUrlEntered.show(resources.dynamicsApp.invalidHostUrlEnteredErrorMessage());
                    results.AppOptionsAreValid = false;
                }
            }
        }
        return results;
    }

    /**
     * Saves app settings and displays a user error if the save does not succeed.
     * @param callback Callback to perform when save has completed.
     */
    export function saveSettings(callback?: (result: Office.AsyncResult<any>) => void): void {
        Office.context.document.settings.set(app.runtimeConfigKey, appState.State.Settings);
        Office.context.document.settings.saveAsync({}, function saveOfficeSettingsResult(asyncResult) {
            if (asyncResult.status === Office.AsyncResultStatus.Failed) {
                telemetry.errors.unableToSaveSettings.show(resources.dynamicsApp.unableToSaveSettingsError(), { Details: resources.dynamicsApp.unableToSaveSettingsDetail() });
            }
            if ($.isFunction(callback)) {
                callback(asyncResult);
            }
        });
    }

    /**
     * Sends a message to the target frame based on provided request and page.
     * @param targetFrame The frame to interact with.
     * @param message The message to send to the frame.
     * @param origin The origin of the frame of which to send the message to.
     * @param cloneData Indicates if data should be cloned.
     */
    export function sendMessage(targetFrame: JQuery, message: any, origin: string, cloneData: boolean = true): void {

        // If the target frame is defined has a content window, continue sending the message; if it doesn't it's likely because the frame was unloaded, so no-op
        if (targetFrame !== undefined && (<HTMLFrameElement>targetFrame[0]).contentWindow) {
            if (cloneData === false) {

                // Store off the message's response type and delete it from the message; otherwise a data clone error will be thrown
                var ResponseType = message.ExpectedResponseType;
                delete message.ExpectedResponseType;

                // Send the message
                (<HTMLFrameElement>targetFrame[0]).contentWindow.postMessage(message, origin);

                // Apply the message's response type after the message has been sent
                message.ExpectedResponseType = ResponseType;
            } else {
                appletMessaging.createCloneableDeepCopy(message, function messageCopied(messageCopy) {

                    // If the target frame has a content window, send the message; if it doesn't it's likely because the frame was unloaded, so no-op
                    if ((<HTMLFrameElement>targetFrame[0]).contentWindow) {
                        (<HTMLFrameElement>targetFrame[0]).contentWindow.postMessage(messageCopy, origin);
                    }
                });
            }
        }
    }

    /**
     * Handles the case where we received an unknown response message.
     * @param event The message event received by the listener.
     */
    export function handleUnknownMessageTypeResponse(event: MessageEvent): void {
        telemetry.errors.unknownAppMessageTypeEncountered.show(resources.dynamicsApp.unknownMessageError());
    }

    /**
     * Toggles an option section from being shown when an option section heading is "clicked".
     * @param event The jQuery encapsulated event from the selected option section heading.
     */
    export function toggleOptionSection(event: JQuery.ClickEvent<HTMLElement>): void {

        // Retrieve the option section
        const heading = $(event.target);
        const section = heading.parent().parent();

        // Determine if the option section is already expanded
        const expanded = heading.attr(util.AriaAttributes.Expanded) !== "false";
        const shouldExpand = !expanded;

        // Toggle the option section's expansion
        section.attr(appElements.GeneralAttributes.expanded, shouldExpand.toString());
        heading.attr(util.AriaAttributes.Expanded, shouldExpand.toString());

        if (shouldExpand) {
            util.setFocus(section.find(".optionSectionContainer"));
        }
    }

    /** Gets the label loader to use in the app */
    export function getLabelLoader(): BaseLabelLoader {
        return labelLoader;
    }

    /**
     * Gets the name of the application CSS file
     * @param styleSheetNamePrefix Name of the Office application to load
     */
    export function getAppCssName(styleSheetNamePrefix: string): string {
        // eslint-disable-next-line security/detect-non-literal-require -- variable is checked before the function call (not user controlled input)
        return require(`./Styles/${styleSheetNamePrefix}DynamicsApp.less`);
    }
}

appletInteraction.app = app;

// Add back-compat functions to the window
if (window && !(window as any).authentication) {
    (window as any).authentication = authentication;
}

if (window && !(window as any).appState) {
    (window as any).appState = appState;
}

if (window && !(window as any).jQuery) {
    (window as any).jQuery = $;
}

if (window && !(window as any).$) {
    (window as any).$ = $;
}
